/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LinkHubPortal.tsx - Portal de Control LinkHub Network
 * ═══════════════════════════════════════════════════════════════════════════
 * @version 1.1.0
 * @date 2025-01-19
 * 
 * Sistema de administración y control de red IRC LinkHub con gestión avanzada
 * de audiencias, charlas, usuarios y monitoreo en tiempo real.
 * 
 * Changelog v1.1.0:
 * - ✅ Contador de usuarios usa MISMA LÓGICA que NetworkTab (allUsers.length)
 * - ✅ Top Canales usa datos reales desde ircChannels
 * - ✅ Estado de la Red dinámico basado en conexión IRC
 * - ✅ Precarga silenciosa de datos (canales + stats + usuarios)
 * - ✅ Listeners completos (messages, events, stats, errors)
 * - ✅ Prevención de duplicación de listeners
 * 
 * Changelog v1.0.2:
 * - Fix nickname: LinkHubPortal## en vez de LinkHub_userId
 * 
 * Changelog v1.0.1:
 * - Stats ahora solo reales (sin fallback a mock data)
 * - Alertas visuales de estado IRC en dashboard
 * - Mejor UX cuando no hay conexión IRC
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Nav, Spinner, Alert, Form, InputGroup, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faNetworkWired, faChartLine, faUsers, faShieldAlt, faFileAlt, faCog,
  faHeadset, faCircle, faPowerOff, faServer, faComments, faCrown,
  faUserShield, faHistory, faBolt, faEye, faExclamationTriangle,
  faCheck, faTimes, faSearch, faFilter, faDownload, faClock,
  faGlobe, faDatabase, faLock, faUnlock, faUserSlash, faTerminal
} from '@fortawesome/free-solid-svg-icons';
import LinkHubNetworkTab from './LinkHubNetworkTab';
import { useUserContext } from '../../layouts/UserContext';
import { getHatateToken } from '../../helpers/utils';
import linkHubIrcService, { IRCChannel, IRCStats, IRCMessage } from '../../service/linkHubIrcService';

const API_TOKEN_KEY = getHatateToken();

/* ========================================================================
   MOCK DATA (fallback cuando no hay conexión)
   ======================================================================== */

const mockUsers: UserInfo[] = Array.from({ length: 5 }, (_, i) => ({
  nickname: `user${i + 1}`,
  hostname: `user${i + 1}@example.com`,
  status: 'online' as const,
  channels: ['#general'],
  privileges: [i === 0 ? 'admin' : i < 3 ? 'operator' : 'user'] as ('admin' | 'operator' | 'user')[],
  joinedAt: new Date().toISOString()
}));

/* ========================================================================
   TIPOS ADICIONALES
   ======================================================================== */

interface IRCUser {
  nick: string;
  host: string;
  privilege: 'owner' | 'op' | 'voice' | 'user';
  idle: number;
}

/* ========================================================================
   TIPOS
   ======================================================================== */

interface ServerStats {
  totalUsers: number;
  activeChannels: number;
  messageRate: number;
  bandwidth: number;
  uptime: number;
  networkLoad: number;
}

interface ChannelInfo {
  name: string;
  users: number;
  topic: string;
  mode: string;
  operators: number;
}

interface UserInfo {
  nickname: string;
  hostname: string;
  status: 'online' | 'away' | 'offline';
  channels: string[];
  privileges: string[];
  joinedAt: string;
}

interface AuditLog {
  timestamp: string;
  type: 'connection' | 'message' | 'command' | 'kick' | 'ban' | 'system';
  user: string;
  action: string;
  details: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
}

/* ========================================================================
   UTILIDADES
   ======================================================================== */

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const formatUptime = (seconds: number): string => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const formatBandwidth = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB/s`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB/s`;
  return `${bytes} B/s`;
};

/* ========================================================================
   MAIN COMPONENT
   ======================================================================== */

const LinkHubPortal: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [serverStatus, setServerStatus] = useState<'online' | 'offline' | 'maintenance'>('online');
  const [loading, setLoading] = useState(false);
  const { user } = useUserContext();
  const API_TOKEN_KEY = getHatateToken();
  
  const [ircConnected, setIrcConnected] = useState(false);
  const [ircStats, setIrcStats] = useState<IRCStats | null>(null);
  const [ircChannels, setIrcChannels] = useState<IRCChannel[]>([]);
  const [ircError, setIrcError] = useState<string | null>(null);
  
  // ✅ NUEVO: States para datos reales
  const [ircUsers, setIrcUsers] = useState<Map<string, IRCUser[]>>(new Map());
  const [ircMessages, setIrcMessages] = useState<Map<string, IRCMessage[]>>(new Map());
  
  // ✅ Sistema de reintentos
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'failed'>('connecting');
  const [retryAttempt, setRetryAttempt] = useState(0);
  
  const listenersAttachedRef = React.useRef(false);
  const retryTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  
  // Configuración de reintentos
  const MAX_RETRY_ATTEMPTS = 6;
  const RETRY_DELAYS = [0, 2000, 4000, 8000, 16000, 30000]; // ~60s total

  // Stats del servidor - DATOS REALES (reactivos)
  const serverStats: ServerStats = {
    totalUsers: 0, // Se calcula en renderDashboard()
    activeChannels: ircChannels.length,
    messageRate: ircStats?.message_rate || 0,
    bandwidth: 1024 * 256,
    uptime: ircStats?.uptime || 0,
    networkLoad: ircStats?.network_load || 0
  };

  const mockLogs: AuditLog[] = [
    {
      timestamp: new Date().toISOString(),
      type: 'connection',
      user: 'user123',
      action: 'CONNECT',
      details: 'Conexión exitosa desde 192.168.1.1',
      severity: 'info'
    },
    {
      timestamp: new Date(Date.now() - 300000).toISOString(),
      type: 'command',
      user: 'admin',
      action: 'KICK',
      details: 'Usuario spam_bot expulsado de #general',
      severity: 'warning'
    }
  ];

  // ═══════════════════════════════════════════════════════════════════════════
  // OBTENER USUARIOS DE UN CANAL
  // ═══════════════════════════════════════════════════════════════════════════
  const fetchChannelUsers = React.useCallback(async (channel: string) => {
    try {
      console.log(`[Portal] 👥 Obteniendo usuarios de ${channel}...`);
      console.log(`[Portal] 🔍 Estado actual: ircConnected=${ircConnected}, ircChannels=${ircChannels.length}`);
      
      const token = API_TOKEN_KEY;
      let userId = 'unknown';
      
      if (token) {
        try {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            userId = payload._id || payload.userId || payload.id || 'unknown';
          }
        } catch (e) {
          console.error('[Portal] Error extrayendo userId del token:', e);
        }
      }
      
      const url = `https://backend.anubisai.net/api/linkhub/users/${encodeURIComponent(channel)}?userId=${userId}`;
      console.log(`[Portal] 📡 Fetching: ${url}`);
      
      const response = await fetch(url, {
        headers: {
          'x-api-key': 'anubis-whatsapp-api-key',
          'Authorization': `${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}`);
      }

      const data = await response.json();
      console.log(`[Portal] 📦 Response:`, data);
      
      if (data.success && data.users) {
        const users: IRCUser[] = data.users.map((u: any) => ({
          nick: u.nick || u.nickname || u.name,
          host: u.host || u.hostname || 'unknown',
          privilege: u.privilege || u.mode || 'user',
          idle: u.idle || 0
        }));
        
        setIrcUsers(prev => {
          const newMap = new Map(prev);
          newMap.set(channel, users);
          console.log(`[Portal] ✅ ${users.length} usuarios cargados de ${channel}`);
          console.log(`[Portal] 📊 Total canales con usuarios: ${newMap.size}`);
          
          // ✅ Calcular total de usuarios únicos
          const uniqueUsers = new Set<string>();
          for (const [ch, usrs] of newMap.entries()) {
            usrs.forEach(u => uniqueUsers.add(u.nick));
          }
          console.log(`[Portal] 👥 Total usuarios únicos en cache: ${uniqueUsers.size}`);
          
          return newMap;
        });
      } else {
        console.warn(`[Portal] ⚠️ Respuesta sin usuarios para ${channel}:`, data);
      }
    } catch (error: any) {
      console.error(`[Portal] ❌ Error obteniendo usuarios de ${channel}:`, error.message);
      console.error(`[Portal] 📋 Stack:`, error.stack);
    }
  }, [API_TOKEN_KEY, ircConnected, ircChannels]);

  // ═══════════════════════════════════════════════════════════════════════════
  // REFRESCAR CANALES Y STATS
  // ═══════════════════════════════════════════════════════════════════════════
  const refreshChannels = React.useCallback(async () => {
    try {
      const channels = await linkHubIrcService.getChannels();
      setIrcChannels(channels);
      console.log(`[Portal] 📺 Canales actualizados: ${channels.length} canales`);
    } catch (error: any) {
      console.error(`[Portal] ❌ Error al obtener canales:`, error.message);
    }
  }, []);

  const refreshStats = React.useCallback(async () => {
    try {
      const stats = await linkHubIrcService.getStats();
      if (stats) {
        setIrcStats(stats);
        console.log(`[Portal] 📊 Stats obtenidos: ${stats.total_users} usuarios`);
      }
    } catch (error: any) {
      console.error(`[Portal] ❌ Error al obtener stats:`, error.message);
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // CONECTAR AL IRC CON REINTENTOS AUTOMÁTICOS
  // ═══════════════════════════════════════════════════════════════════════════
  const connectIrcWithRetry = React.useCallback(async (attemptNumber: number = 0) => {
    // Limpiar timeout previo
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    
    // Si ya llegamos al límite de reintentos
    if (attemptNumber >= MAX_RETRY_ATTEMPTS) {
      console.error('[Portal] ❌ Máximo de reintentos alcanzado');
      setConnectionStatus('failed');
      setIrcError('No se pudo conectar al servidor IRC. Intenta reconectar manualmente.');
      return;
    }
    
    try {
      console.log(`[Portal] 🔌 Intento de conexión ${attemptNumber + 1}/${MAX_RETRY_ATTEMPTS}`);
      setConnectionStatus('connecting');
      setRetryAttempt(attemptNumber + 1);
      
      // Configurar autenticación (mismo patrón que el original)
      linkHubIrcService.setAuth('anubis-whatsapp-api-key', API_TOKEN_KEY || '');
      
      // Conectar
      await linkHubIrcService.connect({
        server: 'irc.linuxcorp.net',
        port: 6697,
        nickname: `LinkHubPortal${Math.floor(Math.random() * 100).toString().padStart(2, '0')}`,
        channels: ['#linkhub', '#general'],
        useSSL: true
      });
      
      setIrcConnected(true);
      setConnectionStatus('connected');
      setIrcError(null);
      setRetryAttempt(0);
      console.log('[Portal] ✅ IRC conectado exitosamente');
      
      // ═══════════════════════════════════════════════════════════════════
      // CONFIGURAR LISTENERS (solo una vez)
      // ═══════════════════════════════════════════════════════════════════
      if (!listenersAttachedRef.current) {
        listenersAttachedRef.current = true;
        
        // Listener para mensajes
        linkHubIrcService.onMessage((message) => {
          const channel = message.channel || 'server';
          setIrcMessages(prev => {
            const newMap = new Map(prev);
            const channelMsgs = newMap.get(channel) || [];
            
            const messageExists = channelMsgs.some(msg => 
              msg.id === message.id || 
              (msg.timestamp === message.timestamp && msg.text === message.text && msg.from === message.from)
            );
            
            if (!messageExists) {
              newMap.set(channel, [...channelMsgs, message]);
            }
            
            return newMap;
          });
        });
        
        // Listener para eventos
        linkHubIrcService.onEvent((event) => {
          console.log(`[Portal] 📢 Evento IRC: ${event.type}`);
          
          if (event.type === 'JOIN' || event.type === 'PART') {
            // ✅ Actualizar canales inmediatamente
            setTimeout(() => refreshChannels(), 500);
            
            // ✅ Si es un canal específico, actualizar usuarios de ese canal
            if (event.channel) {
              console.log(`[Portal] 👥 Actualizando usuarios de ${event.channel} por ${event.type}...`);
              setTimeout(() => fetchChannelUsers(event.channel!), 1000);
            } else {
              // ✅ Si no hay canal específico, actualizar TODOS los canales
              console.log('[Portal] 👥 Actualizando TODOS los usuarios por evento global...');
              setTimeout(async () => {
                const channels = await linkHubIrcService.getChannels();
                for (const channel of channels) {
                  await fetchChannelUsers(channel.name);
                  await new Promise(r => setTimeout(r, 200));
                }
              }, 1000);
            }
          }
          
          if (event.type === 'USERLIST' && event.channel) {
            console.log(`[Portal] 📋 USERLIST recibido para ${event.channel}`);
            setTimeout(() => fetchChannelUsers(event.channel!), 1500);
          }
          
          // ✅ NUEVO: Actualizar stats cuando hay cambios de usuarios
          if (event.type === 'JOIN' || event.type === 'PART' || event.type === 'QUIT') {
            setTimeout(() => refreshStats(), 2000);
          }
        });
        
        // Listener para estadísticas
        linkHubIrcService.onStats((stats) => {
          setIrcStats(stats);
          console.log(`[Portal] 📊 Stats actualizados: ${stats.total_users} usuarios`);
        });
        
        // Listener para errores
        linkHubIrcService.onError((error) => {
          console.error('[Portal] ❌ Error IRC:', error);
          setIrcError(error.text || 'Error desconocido');
        });
        
        console.log('[Portal] ✅ Listeners configurados correctamente');
      }
      
      // ═══════════════════════════════════════════════════════════════════
      // PRECARGA SILENCIOSA DE DATOS
      // ═══════════════════════════════════════════════════════════════════
      setTimeout(async () => {
        console.log('[Portal] 🔄 Iniciando precarga silenciosa...');
        console.log(`[Portal] 📊 Estado: connectionStatus=${connectionStatus}, listenersAttached=${listenersAttachedRef.current}`);
        
        // 1. Refrescar canales
        console.log('[Portal] 📺 Precarga: refrescando canales...');
        await refreshChannels();
        
        // 2. Refrescar stats
        console.log('[Portal] 📊 Precarga: refrescando stats...');
        await refreshStats();
        
        // 3. Cargar usuarios de TODOS los canales
        const channels = await linkHubIrcService.getChannels();
        console.log(`[Portal] 📺 Precarga: ${channels.length} canales detectados`);
        console.log(`[Portal] 📋 Canales:`, channels.map(c => c.name).join(', '));
        
        if (channels.length === 0) {
          console.warn('[Portal] ⚠️ No se detectaron canales en la precarga');
        }
        
        for (const channel of channels) {
          console.log(`[Portal] 👥 Precargando usuarios de ${channel.name}...`);
          await fetchChannelUsers(channel.name);
          await new Promise(r => setTimeout(r, 300));
        }
        
        console.log('[Portal] ✅ Precarga silenciosa completada');
        
        // ✅ Log final del estado
        setTimeout(() => {
          console.log('[Portal] 📊 Estado final después de precarga:');
          console.log(`  - Canales: ${ircChannels.length}`);
          console.log(`  - ircUsers.size: ${ircUsers.size}`);
          
          const uniqueUsers = new Set<string>();
          for (const [ch, usrs] of ircUsers.entries()) {
            console.log(`  - ${ch}: ${usrs.length} usuarios`);
            usrs.forEach(u => uniqueUsers.add(u.nick));
          }
          console.log(`  - Total usuarios únicos: ${uniqueUsers.size}`);
        }, 1000);
      }, 2000);
      
    } catch (error: any) {
      console.error(`[Portal] ❌ Error en intento ${attemptNumber + 1}:`, error);
      setIrcConnected(false);
      setIrcError(error.message || 'Error al conectar');
      
      // Programar siguiente reintento
      const nextAttempt = attemptNumber + 1;
      if (nextAttempt < MAX_RETRY_ATTEMPTS) {
        const delay = RETRY_DELAYS[nextAttempt];
        console.log(`[Portal] 🔄 Reintentando en ${delay}ms...`);
        
        retryTimeoutRef.current = setTimeout(() => {
          connectIrcWithRetry(nextAttempt);
        }, delay);
      } else {
        setConnectionStatus('failed');
      }
    }
  }, [fetchChannelUsers, refreshChannels, refreshStats]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // AUTO-CONEXIÓN AL MONTAR
  // ═══════════════════════════════════════════════════════════════════════════
  React.useEffect(() => {
    console.log('[Portal] 🚀 Componente montado - Iniciando auto-conexión...');
    connectIrcWithRetry(0);
    
    // Cleanup al desmontar
    return () => {
      console.log('[Portal] 🔌 Componente desmontado - Limpiando...');
      
      // Limpiar timeout de reintentos
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      
      // Desconectar IRC
      if (ircConnected) {
        linkHubIrcService.disconnect();
      }
      
      // Reset listeners flag
      listenersAttachedRef.current = false;
    };
  }, []); // ✅ Sin dependencias - ejecuta UNA VEZ al montar

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTO-REFRESH PERIÓDICO DE USUARIOS (cada 30 segundos)
  // ═══════════════════════════════════════════════════════════════════════════
  React.useEffect(() => {
    if (!ircConnected || ircChannels.length === 0) {
      console.log('[Portal] ⚠️ No conectado o sin canales - saltando auto-refresh');
      return;
    }
    
    let isActive = true;
    
    const refreshAllUsers = async () => {
      if (!isActive) return;
      
      console.log('[Portal] 🔄 Auto-refresh de usuarios iniciado...');
      
      for (const channel of ircChannels) {
        if (!isActive) break;
        
        try {
          await fetchChannelUsers(channel.name);
          console.log(`[Portal] ✅ Usuarios de ${channel.name} actualizados`);
        } catch (error: any) {
          console.error(`[Portal] ❌ Error actualizando ${channel.name}:`, error.message);
        }
        
        // Pequeño delay entre canales
        await new Promise(r => setTimeout(r, 200));
      }
      
      console.log('[Portal] ✅ Auto-refresh completado');
    };
    
    // Primera carga inmediata
    refreshAllUsers();
    
    // Auto-refresh cada 30 segundos
    const interval = setInterval(() => {
      if (isActive) {
        refreshAllUsers();
      }
    }, 30000);
    
    return () => {
      isActive = false;
      clearInterval(interval);
      console.log('[Portal] 🧹 Auto-refresh detenido');
    };
  }, [ircConnected, ircChannels, fetchChannelUsers]); // ✅ Re-ejecuta cuando cambian canales o conexión

  /* ====================================================================
     TAB: DASHBOARD
     ==================================================================== */
  const renderDashboard = () => {
    // ═══════════════════════════════════════════════════════════════════════════
    // GENERAR LISTA DE USUARIOS DESDE IRC REAL (IDÉNTICO a NetworkTab)
    // ═══════════════════════════════════════════════════════════════════════════
    let allUsers: UserInfo[] = [];
    
    if (ircConnected && ircUsers.size > 0) {
      // Convertir Map<channel, IRCUser[]> a UserInfo[]
      const usersMap = new Map<string, UserInfo>();
      
      for (const [channel, channelUsers] of ircUsers) {
        for (const ircUser of channelUsers) {
          const existingUser = usersMap.get(ircUser.nick);
          
          if (existingUser) {
            // Agregar canal si no está
            if (!existingUser.channels.includes(channel)) {
              existingUser.channels.push(channel);
            }
          } else {
            // Determinar privilegio basado en modes
            let privilege: 'admin' | 'operator' | 'user' = 'user';
            if (ircUser.privilege === 'owner') {
              privilege = 'admin';
            } else if (ircUser.privilege === 'op') {
              privilege = 'operator';
            }
            
            usersMap.set(ircUser.nick, {
              nickname: ircUser.nick,
              hostname: ircUser.host || 'unknown',
              status: 'online',
              channels: [channel],
              privileges: [privilege],
              joinedAt: new Date().toISOString()
            });
          }
        }
      }
      
      allUsers = Array.from(usersMap.values());
      console.log(`[Portal] 👥 Total usuarios únicos calculados: ${allUsers.length}`);
    } else {
      console.log(`[Portal] ⚠️ No hay datos de usuarios - ircConnected: ${ircConnected}, ircUsers.size: ${ircUsers.size}`);
      // ✅ NO usar mock - dejar array vacío para mostrar estado real
      allUsers = [];
    }

    const totalUsers = allUsers.length;

    // Actualizar serverStats con totalUsers calculado
    const currentServerStats = {
      ...serverStats,
      totalUsers: totalUsers
    };

    // Calcular realChannels DENTRO del render
    const realChannels: ChannelInfo[] = ircChannels.map(ch => {
      const channelUsers = ircUsers.get(ch.name) || [];
      const operators = channelUsers.filter(u => 
        u.privilege === 'owner' || u.privilege === 'op'
      ).length;
      
      return {
        name: ch.name,
        users: channelUsers.length,
        topic: ch.topic || 'Sin topic',
        mode: '+nt',
        operators
      };
    });

    return (
    <div className="tab-content-wrapper">
      {/* ✅ Alertas según estado de conexión */}
      {connectionStatus === 'connecting' && (
        <Alert variant="info" className="mb-3">
          <Spinner size="sm" animation="border" className="me-2" />
          <strong>Conectando al servidor IRC...</strong> Intento {retryAttempt} de {MAX_RETRY_ATTEMPTS}
        </Alert>
      )}
      
      {connectionStatus === 'failed' && (
        <Alert variant="danger" className="mb-3 d-flex justify-content-between align-items-center">
          <div>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <strong>No se pudo conectar al servidor IRC.</strong> Los datos mostrados no están actualizados.
          </div>
          <Button 
            variant="outline-danger" 
            size="sm" 
            onClick={() => connectIrcWithRetry(0)}
            style={{ borderRadius: '6px' }}
          >
            <FontAwesomeIcon icon={faPowerOff} className="me-2" />
            Reconectar
          </Button>
        </Alert>
      )}
      
      {ircError && connectionStatus !== 'failed' && (
        <Alert variant="warning" className="mb-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          <strong>Advertencia:</strong> {ircError}
        </Alert>
      )}
      
      {/* Stats Cards */}
      <div className="stats-grid">
        <Card className="stat-card">
          <Card.Body>
            <div className="stat-header">
              <div className="stat-icon" style={{ backgroundColor: 'rgba(0, 122, 255, 0.12)' }}>
                <FontAwesomeIcon icon={faUsers} style={{ color: '#007AFF' }} />
              </div>
              <Badge bg="success" style={{ fontSize: '10px', borderRadius: '6px' }}>
                <FontAwesomeIcon icon={faCircle} size="xs" className="me-1" />
                {connectionStatus === 'connected' ? 'Online' : connectionStatus === 'connecting' ? 'Conectando...' : 'Offline'}
              </Badge>
            </div>
            <div className="stat-value">
              {connectionStatus === 'connecting' ? (
                <Spinner animation="border" size="sm" />
              ) : (
                totalUsers
              )}
            </div>
            <div className="stat-label">
              Usuarios Conectados
              {connectionStatus === 'connected' && totalUsers === 0 && (
                <small style={{ display: 'block', color: '#FF9500', marginTop: '4px', fontSize: '10px' }}>
                  Cargando datos...
                </small>
              )}
            </div>
            <ProgressBar 
              now={Math.min((totalUsers / 100) * 100, 100)} 
              style={{ height: '4px', marginTop: '12px', backgroundColor: 'rgba(0, 122, 255, 0.15)' }}
            >
              <ProgressBar now={Math.min((totalUsers / 100) * 100, 100)} style={{ backgroundColor: '#007AFF' }} />
            </ProgressBar>
          </Card.Body>
        </Card>

        <Card className="stat-card">
          <Card.Body>
            <div className="stat-header">
              <div className="stat-icon" style={{ backgroundColor: 'rgba(52, 199, 89, 0.12)' }}>
                <FontAwesomeIcon icon={faComments} style={{ color: '#34C759' }} />
              </div>
              <Badge bg="primary" style={{ fontSize: '10px', borderRadius: '6px' }}>
                Activos
              </Badge>
            </div>
            <div className="stat-value">{ircChannels.length}</div>
            <div className="stat-label">Canales Activos</div>
            <ProgressBar 
              now={Math.min((ircChannels.length / 10) * 100, 100)} 
              style={{ height: '4px', marginTop: '12px', backgroundColor: 'rgba(52, 199, 89, 0.15)' }}
            >
              <ProgressBar now={Math.min((ircChannels.length / 10) * 100, 100)} style={{ backgroundColor: '#34C759' }} />
            </ProgressBar>
          </Card.Body>
        </Card>

        <Card className="stat-card">
          <Card.Body>
            <div className="stat-header">
              <div className="stat-icon" style={{ backgroundColor: 'rgba(255, 149, 0, 0.12)' }}>
                <FontAwesomeIcon icon={faBolt} style={{ color: '#FF9500' }} />
              </div>
              <span className="stat-trend">+12%</span>
            </div>
            <div className="stat-value">{currentServerStats.messageRate}/s</div>
            <div className="stat-label">Mensajes por Segundo</div>
            <ProgressBar 
              now={Math.min((currentServerStats.messageRate / 100) * 100, 100)} 
              style={{ height: '4px', marginTop: '12px', backgroundColor: 'rgba(255, 149, 0, 0.15)' }}
            >
              <ProgressBar now={Math.min((currentServerStats.messageRate / 100) * 100, 100)} style={{ backgroundColor: '#FF9500' }} />
            </ProgressBar>
          </Card.Body>
        </Card>

        <Card className="stat-card">
          <Card.Body>
            <div className="stat-header">
              <div className="stat-icon" style={{ backgroundColor: 'rgba(90, 200, 250, 0.12)' }}>
                <FontAwesomeIcon icon={faServer} style={{ color: '#5AC8FA' }} />
              </div>
              <Badge bg="info" style={{ fontSize: '10px', borderRadius: '6px' }}>
                {formatUptime(currentServerStats.uptime)}
              </Badge>
            </div>
            <div className="stat-value">{currentServerStats.networkLoad}%</div>
            <div className="stat-label">Carga de Red</div>
            <ProgressBar 
              now={currentServerStats.networkLoad} 
              style={{ height: '4px', marginTop: '12px', backgroundColor: 'rgba(90, 200, 250, 0.15)' }}
            >
              <ProgressBar now={currentServerStats.networkLoad} style={{ backgroundColor: '#5AC8FA' }} />
            </ProgressBar>
          </Card.Body>
        </Card>
      </div>

      {/* Network Overview */}
      <div className="content-grid">
        <Card className="overview-card">
          <Card.Header>
            <div className="card-header-content">
              <FontAwesomeIcon icon={faNetworkWired} className="me-2" style={{ color: '#007AFF' }} />
              <span>Estado de la Red</span>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="network-status-grid">
              <div className="network-status-item">
                <div className="status-indicator" style={{ 
                  backgroundColor: 
                    connectionStatus === 'connected' ? '#34C759' : 
                    connectionStatus === 'connecting' ? '#FF9500' : 
                    '#FF3B30' 
                }}></div>
                <div className="status-info">
                  <div className="status-label">Servidor IRC</div>
                  <div className="status-value">
                    {connectionStatus === 'connected' ? 'Conectado' : 
                     connectionStatus === 'connecting' ? `Conectando (${retryAttempt}/${MAX_RETRY_ATTEMPTS})` : 
                     'Desconectado'}
                  </div>
                </div>
              </div>
              <div className="network-status-item">
                <div className="status-indicator" style={{ 
                  backgroundColor: ircStats ? '#34C759' : '#8E8E93' 
                }}></div>
                <div className="status-info">
                  <div className="status-label">Estadísticas</div>
                  <div className="status-value">
                    {ircStats ? 'Activo' : 'Sin datos'}
                  </div>
                </div>
              </div>
              <div className="network-status-item">
                <div className="status-indicator" style={{ 
                  backgroundColor: ircChannels.length > 0 ? '#34C759' : '#FF9500' 
                }}></div>
                <div className="status-info">
                  <div className="status-label">Canales</div>
                  <div className="status-value">
                    {ircChannels.length > 0 ? `${ircChannels.length} activos` : 'Sin canales'}
                  </div>
                </div>
              </div>
              <div className="network-status-item">
                <div className="status-indicator" style={{ 
                  backgroundColor: allUsers.length > 0 ? '#34C759' : '#8E8E93' 
                }}></div>
                <div className="status-info">
                  <div className="status-label">Usuarios</div>
                  <div className="status-value">
                    {allUsers.length > 0 ? `${allUsers.length} online` : 'Sin usuarios'}
                  </div>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card className="overview-card">
          <Card.Header>
            <div className="card-header-content">
              <FontAwesomeIcon icon={faChartLine} className="me-2" style={{ color: '#34C759' }} />
              <span>Top Canales</span>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="channels-list">
              {realChannels.length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '20px', 
                  color: '#8E8E93',
                  fontSize: '13px' 
                }}>
                  {connectionStatus === 'connecting' ? (
                    <>
                      <Spinner size="sm" animation="border" className="mb-2" />
                      <div>Conectando al IRC...</div>
                    </>
                  ) : connectionStatus === 'failed' ? (
                    <>
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mb-2" style={{ fontSize: '20px', color: '#FF3B30' }} />
                      <div>Error al conectar. Intenta reconectar.</div>
                    </>
                  ) : (
                    'Sin canales disponibles'
                  )}
                </div>
              )}
              {realChannels.map((channel, idx) => (
                <div key={idx} className="channel-item">
                  <div className="channel-info">
                    <div className="channel-name">
                      <FontAwesomeIcon icon={faComments} className="me-2" style={{ color: '#007AFF', fontSize: '12px' }} />
                      {channel.name}
                    </div>
                    <div className="channel-stats">
                      <span className="stat">{channel.users} usuarios</span>
                      <span className="stat">{channel.operators} ops</span>
                    </div>
                  </div>
                  <Badge bg="secondary" style={{ fontSize: '10px' }}>{channel.mode}</Badge>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
    );
  };

  /* ====================================================================
     TAB: LINKHUB NETWORK
     ==================================================================== */
  const renderNetwork = () => <LinkHubNetworkTab />;

  /* ====================================================================
     TAB: ADMINISTRACION
     ==================================================================== */
  const renderAdministration = () => {
    // ═══════════════════════════════════════════════════════════════════════════
    // GENERAR LISTA DE USUARIOS DESDE IRC REAL (IDÉNTICO a NetworkTab)
    // ═══════════════════════════════════════════════════════════════════════════
    let allUsers: UserInfo[] = [];
    
    if (ircConnected && ircUsers.size > 0) {
      const usersMap = new Map<string, UserInfo>();
      
      for (const [channel, channelUsers] of ircUsers) {
        for (const ircUser of channelUsers) {
          const existingUser = usersMap.get(ircUser.nick);
          
          if (existingUser) {
            if (!existingUser.channels.includes(channel)) {
              existingUser.channels.push(channel);
            }
          } else {
            let privilege: 'admin' | 'operator' | 'user' = 'user';
            if (ircUser.privilege === 'owner') {
              privilege = 'admin';
            } else if (ircUser.privilege === 'op') {
              privilege = 'operator';
            }
            
            usersMap.set(ircUser.nick, {
              nickname: ircUser.nick,
              hostname: ircUser.host || 'unknown',
              status: 'online',
              channels: [channel],
              privileges: [privilege],
              joinedAt: new Date().toISOString()
            });
          }
        }
      }
      
      allUsers = Array.from(usersMap.values());
      console.log(`[Portal/Admin] 👥 Total usuarios únicos: ${allUsers.length}`);
    } else {
      console.log(`[Portal/Admin] ⚠️ Sin datos - ircConnected: ${ircConnected}, ircUsers.size: ${ircUsers.size}`);
      // ✅ NO usar mock - mostrar estado real vacío
      allUsers = [];
    }

    return (
    <div className="tab-content-wrapper">
      <div className="admin-controls-grid">
        <Card className="admin-card">
          <Card.Header>
            <div className="card-header-content">
              <FontAwesomeIcon icon={faUserShield} className="me-2" style={{ color: '#007AFF' }} />
              <span>Gestión de Privilegios</span>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="users-list">
              {allUsers.length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '20px', 
                  color: '#8E8E93',
                  fontSize: '13px' 
                }}>
                  {connectionStatus === 'connecting' ? (
                    <>
                      <Spinner size="sm" animation="border" className="mb-2" />
                      <div>Cargando usuarios...</div>
                    </>
                  ) : connectionStatus === 'failed' ? (
                    <>
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mb-2" style={{ fontSize: '20px', color: '#FF3B30' }} />
                      <div>Error al conectar. Intenta reconectar.</div>
                    </>
                  ) : (
                    'Sin usuarios disponibles'
                  )}
                </div>
              )}
              {allUsers.map((user, idx) => (
                <div key={idx} className="user-item">
                  <div className="user-info">
                    <div className="user-avatar" style={{ 
                      backgroundColor: user.privileges.includes('admin') ? '#007AFF' : '#8E8E93' 
                    }}>
                      <FontAwesomeIcon 
                        icon={user.privileges.includes('admin') ? faCrown : faUsers} 
                        style={{ color: 'white', fontSize: '12px' }}
                      />
                    </div>
                    <div className="user-details">
                      <div className="user-nickname">{user.nickname}</div>
                      <div className="user-host">{user.hostname}</div>
                    </div>
                  </div>
                  <div className="user-badges">
                    {user.privileges.map((priv, pidx) => (
                      <Badge 
                        key={pidx}
                        bg={priv === 'admin' ? 'primary' : 'secondary'}
                        style={{ fontSize: '10px', marginLeft: '4px' }}
                      >
                        {priv}
                      </Badge>
                    ))}
                  </div>
                  <div className="user-status">
                    <FontAwesomeIcon 
                      icon={faCircle} 
                      style={{ 
                        color: user.status === 'online' ? '#34C759' : '#8E8E93',
                        fontSize: '8px'
                      }} 
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>

        <Card className="admin-card">
          <Card.Header>
            <div className="card-header-content">
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" style={{ color: '#34C759' }} />
              <span>Acciones Administrativas</span>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="admin-actions">
              <Button variant="outline-primary" className="admin-action-btn">
                <FontAwesomeIcon icon={faLock} className="me-2" />
                Bloquear Usuario
              </Button>
              <Button variant="outline-success" className="admin-action-btn">
                <FontAwesomeIcon icon={faUnlock} className="me-2" />
                Desbloquear Usuario
              </Button>
              <Button variant="outline-warning" className="admin-action-btn">
                <FontAwesomeIcon icon={faUserSlash} className="me-2" />
                Expulsar de Canal
              </Button>
              <Button variant="outline-danger" className="admin-action-btn">
                <FontAwesomeIcon icon={faTimes} className="me-2" />
                Ban Permanente
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
    );
  };

  /* ====================================================================
     TAB: LOG AUDITOR
     ==================================================================== */
  const renderAuditor = () => (
    <div className="tab-content-wrapper">
      <div className="auditor-controls">
        <InputGroup style={{ maxWidth: '400px' }}>
          <InputGroup.Text style={{ background: 'var(--bs-gray-100)', borderRadius: '8px 0 0 8px' }}>
            <FontAwesomeIcon icon={faSearch} style={{ color: '#8E8E93' }} />
          </InputGroup.Text>
          <Form.Control 
            type="text" 
            placeholder="Buscar en logs..." 
            style={{ borderLeft: 'none', borderRadius: '0 8px 8px 0' }}
          />
        </InputGroup>

        <div className="filter-controls">
          <Button variant="outline-secondary" size="sm" style={{ borderRadius: '8px' }}>
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            Filtros
          </Button>
          <Button variant="outline-primary" size="sm" style={{ borderRadius: '8px' }}>
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            Exportar
          </Button>
        </div>
      </div>

      <Card className="logs-card">
        <Card.Body className="p-0">
          <div className="logs-table">
            <div className="logs-header">
              <div className="log-col-time">Timestamp</div>
              <div className="log-col-type">Tipo</div>
              <div className="log-col-user">Usuario</div>
              <div className="log-col-action">Acción</div>
              <div className="log-col-severity">Severidad</div>
            </div>
            {mockLogs.map((log, idx) => (
              <div key={idx} className="log-row">
                <div className="log-col-time">
                  <FontAwesomeIcon icon={faClock} className="me-2" style={{ color: '#8E8E93', fontSize: '11px' }} />
                  {new Date(log.timestamp).toLocaleTimeString()}
                </div>
                <div className="log-col-type">
                  <Badge bg="secondary" style={{ fontSize: '10px' }}>
                    {log.type}
                  </Badge>
                </div>
                <div className="log-col-user">{log.user}</div>
                <div className="log-col-action">{log.action}</div>
                <div className="log-col-severity">
                  <Badge 
                    bg={
                      log.severity === 'critical' ? 'danger' :
                      log.severity === 'error' ? 'danger' :
                      log.severity === 'warning' ? 'warning' : 'info'
                    }
                    style={{ fontSize: '10px' }}
                  >
                    {log.severity}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  /* ====================================================================
     TAB: CONFIGURACION
     ==================================================================== */
  const renderConfiguration = () => (
    <div className="tab-content-wrapper">
      <div className="config-grid">
        <Card className="config-card">
          <Card.Header>
            <div className="card-header-content">
              <FontAwesomeIcon icon={faPowerOff} className="me-2" style={{ color: '#FF3B30' }} />
              <span>Control de Red</span>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="power-controls">
              <div className="power-status">
                <div className="status-indicator-large" style={{ 
                  backgroundColor: serverStatus === 'online' ? '#34C759' : '#FF3B30',
                  boxShadow: serverStatus === 'online' ? '0 0 20px rgba(52, 199, 89, 0.4)' : '0 0 20px rgba(255, 59, 48, 0.4)'
                }}>
                  <FontAwesomeIcon 
                    icon={faPowerOff} 
                    style={{ color: 'white', fontSize: '32px' }}
                  />
                </div>
                <div className="status-text">
                  <div className="status-label">Estado del Servidor</div>
                  <div className="status-value" style={{ 
                    color: serverStatus === 'online' ? '#34C759' : '#FF3B30' 
                  }}>
                    {serverStatus === 'online' ? 'ONLINE' : 'OFFLINE'}
                  </div>
                </div>
              </div>

              <div className="power-buttons">
                <Button 
                  variant={serverStatus === 'online' ? 'outline-success' : 'success'}
                  size="lg"
                  className="power-btn"
                  disabled={serverStatus === 'online'}
                >
                  <FontAwesomeIcon icon={faCheck} className="me-2" />
                  Iniciar Red
                </Button>
                <Button 
                  variant={serverStatus === 'offline' ? 'outline-danger' : 'danger'}
                  size="lg"
                  className="power-btn"
                  disabled={serverStatus === 'offline'}
                >
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Detener Red
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card className="config-card">
          <Card.Header>
            <div className="card-header-content">
              <FontAwesomeIcon icon={faDatabase} className="me-2" style={{ color: '#007AFF' }} />
              <span>Configuración del Sistema</span>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="config-options">
              <div className="config-option">
                <div className="config-option-info">
                  <div className="config-option-label">Modo Mantenimiento</div>
                  <div className="config-option-description">Deshabilitar conexiones de usuarios</div>
                </div>
                <Form.Check type="switch" />
              </div>
              <div className="config-option">
                <div className="config-option-info">
                  <div className="config-option-label">Auto-Moderación</div>
                  <div className="config-option-description">Filtrado automático de contenido</div>
                </div>
                <Form.Check type="switch" defaultChecked />
              </div>
              <div className="config-option">
                <div className="config-option-info">
                  <div className="config-option-label">Logging Extendido</div>
                  <div className="config-option-description">Guardar logs detallados de actividad</div>
                </div>
                <Form.Check type="switch" defaultChecked />
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );

  /* ====================================================================
     TAB: SOPORTE
     ==================================================================== */
  const renderSupport = () => (
    <div className="tab-content-wrapper">
      <div className="support-placeholder">
        <div className="placeholder-icon">
          <FontAwesomeIcon icon={faHeadset} style={{ fontSize: '64px', color: '#34C759', opacity: 0.3 }} />
        </div>
        <h5 style={{ color: 'var(--bs-secondary-color)', fontWeight: '600', marginTop: '16px' }}>
          Centro de Soporte
        </h5>
        <p style={{ color: 'var(--bs-secondary-color)', fontSize: '13px', maxWidth: '400px', textAlign: 'center' }}>
          Documentación, tickets de soporte y recursos para administradores del sistema LinkHub.
        </p>
        <Button variant="primary" style={{ marginTop: '16px', borderRadius: '8px' }}>
          <FontAwesomeIcon icon={faHeadset} className="me-2" />
          Contactar Soporte
        </Button>
      </div>
    </div>
  );

  /* ====================================================================
     RENDER PRINCIPAL
     ==================================================================== */
  return (
    <div className="linkhub-portal">
      {/* Header */}
      <div className="portal-header">
        <div className="portal-title-section">
          <h2 className="portal-title">
            <FontAwesomeIcon icon={faNetworkWired} className="me-3" style={{ color: '#007AFF' }} />
            LinkHub Portal
          </h2>
          <div className="portal-status">
            <Badge 
              bg={serverStatus === 'online' ? 'success' : 'danger'}
              style={{ 
                fontSize: '11px', 
                fontWeight: '600', 
                borderRadius: '6px', 
                padding: '5px 10px'
              }}
            >
              <FontAwesomeIcon icon={faCircle} size="xs" className="me-1" />
              {serverStatus === 'online' ? 'Red Activa' : 'Red Inactiva'}
            </Badge>
            {/* Indicador IRC con estados */}
            {connectionStatus === 'connecting' && (
              <Badge 
                bg="warning"
                style={{ 
                  fontSize: '11px', 
                  fontWeight: '600', 
                  borderRadius: '6px', 
                  padding: '5px 10px'
                }}
              >
                <Spinner size="sm" animation="border" style={{ width: '10px', height: '10px', marginRight: '6px' }} />
                IRC: Conectando... ({retryAttempt}/{MAX_RETRY_ATTEMPTS})
              </Badge>
            )}
            {connectionStatus === 'connected' && (
              <Badge 
                bg="success"
                style={{ 
                  fontSize: '11px', 
                  fontWeight: '600', 
                  borderRadius: '6px', 
                  padding: '5px 10px'
                }}
              >
                <FontAwesomeIcon icon={faServer} size="xs" className="me-1" />
                IRC: Conectado
              </Badge>
            )}
            {connectionStatus === 'failed' && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => connectIrcWithRetry(0)}
                style={{ 
                  fontSize: '11px', 
                  fontWeight: '600', 
                  borderRadius: '6px', 
                  padding: '5px 10px'
                }}
              >
                <FontAwesomeIcon icon={faExclamationTriangle} size="xs" className="me-1" />
                Reconectar IRC
              </Button>
            )}
            <span className="status-text">
              <FontAwesomeIcon icon={faClock} className="me-1" />
              {new Date().toLocaleTimeString()}
            </span>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="portal-navigation">
        <Nav variant="pills" className="portal-tabs">
          <Nav.Item>
            <Nav.Link 
              active={activeTab === 'dashboard'} 
              onClick={() => setActiveTab('dashboard')}
              className="portal-tab"
            >
              <FontAwesomeIcon icon={faChartLine} className="me-2" />
              Dashboard
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              active={activeTab === 'network'} 
              onClick={() => setActiveTab('network')}
              className="portal-tab"
            >
              <FontAwesomeIcon icon={faNetworkWired} className="me-2" />
              LinkHub Network
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              active={activeTab === 'admin'} 
              onClick={() => setActiveTab('admin')}
              className="portal-tab"
              disabled
            >
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
              Administración
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              active={activeTab === 'auditor'} 
              onClick={() => setActiveTab('auditor')}
              className="portal-tab"
              disabled
            >
              <FontAwesomeIcon icon={faFileAlt} className="me-2" />
              Log Auditor
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              active={activeTab === 'config'} 
              onClick={() => setActiveTab('config')}
              className="portal-tab"
              disabled
            >
              <FontAwesomeIcon icon={faCog} className="me-2" />
              Configuración
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link 
              active={activeTab === 'support'} 
              onClick={() => setActiveTab('support')}
              className="portal-tab"
              disabled
            >
              <FontAwesomeIcon icon={faHeadset} className="me-2" />
              Soporte
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </div>

      {/* Content Area */}
      <div className="portal-content">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'network' && renderNetwork()}
        {activeTab === 'admin' && renderAdministration()}
        {activeTab === 'auditor' && renderAuditor()}
        {activeTab === 'config' && renderConfiguration()}
        {activeTab === 'support' && renderSupport()}
      </div>

      {/* Styles */}
      <style>{`
        .linkhub-portal {
          padding: 24px;
          background: var(--bs-body-bg);
          min-height: 100vh;
        }

        .portal-header {
          background: var(--bs-card-bg);
          border-radius: 12px;
          padding: 20px 24px;
          margin-bottom: 20px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
          border: 1px solid var(--bs-border-color);
        }

        .portal-title {
          font-size: 22px;
          font-weight: 600;
          color: var(--bs-body-color);
          margin: 0;
          letter-spacing: -0.3px;
        }

        .portal-status {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 8px;
        }

        .status-text {
          font-size: 13px;
          color: var(--bs-secondary-color);
          font-weight: 500;
        }

        .portal-navigation {
          background: var(--bs-card-bg);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 20px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
          border: 1px solid var(--bs-border-color);
        }

        .portal-tabs {
          gap: 8px;
          flex-wrap: wrap;
        }

        .portal-tab {
          border-radius: 8px !important;
          font-size: 13px !important;
          font-weight: 500 !important;
          padding: 10px 16px !important;
          color: var(--bs-body-color) !important;
          border: 1px solid transparent !important;
          transition: all 0.2s ease !important;
        }

        .portal-tab:hover {
          background: rgba(0, 122, 255, 0.08) !important;
          border-color: rgba(0, 122, 255, 0.2) !important;
        }

        .portal-tab.active {
          background: #007AFF !important;
          color: white !important;
          border-color: #007AFF !important;
        }

        .portal-content {
          background: var(--bs-card-bg);
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
          border: 1px solid var(--bs-border-color);
          min-height: 500px;
        }

        .tab-content-wrapper {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 16px;
          margin-bottom: 20px;
        }

        .stat-card {
          border: 1px solid var(--bs-border-color);
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          border-color: rgba(0, 122, 255, 0.2);
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .stat-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: var(--bs-body-color);
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 12px;
          color: var(--bs-secondary-color);
          font-weight: 500;
        }

        .stat-trend {
          font-size: 11px;
          color: #34C759;
          font-weight: 600;
        }

        .content-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 16px;
        }

        .overview-card {
          border: 1px solid var(--bs-border-color);
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }

        .overview-card .card-header {
          background: var(--bs-card-bg);
          border-bottom: 1px solid var(--bs-border-color);
          padding: 14px 16px;
        }

        .card-header-content {
          font-size: 14px;
          font-weight: 600;
          color: var(--bs-body-color);
          display: flex;
          align-items: center;
        }

        .network-status-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .network-status-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: var(--bs-gray-100);
          border-radius: 8px;
        }

        .status-indicator {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          flex-shrink: 0;
          box-shadow: 0 0 8px currentColor;
        }

        .status-info {
          flex: 1;
        }

        .status-label {
          font-size: 11px;
          color: var(--bs-secondary-color);
          font-weight: 500;
        }

        .status-value {
          font-size: 13px;
          color: var(--bs-body-color);
          font-weight: 600;
        }

        .channels-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .channel-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px;
          background: var(--bs-gray-100);
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .channel-item:hover {
          background: var(--bs-gray-200);
          transform: translateX(2px);
        }

        .channel-info {
          flex: 1;
        }

        .channel-name {
          font-size: 13px;
          font-weight: 600;
          color: var(--bs-body-color);
          margin-bottom: 4px;
        }

        .channel-stats {
          font-size: 11px;
          color: var(--bs-secondary-color);
          display: flex;
          gap: 12px;
        }

        .channel-stats .stat {
          font-weight: 500;
        }

        .network-placeholder,
        .support-placeholder {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
        }

        .placeholder-icon {
          margin-bottom: 20px;
        }

        .admin-controls-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 16px;
        }

        .admin-card {
          border: 1px solid var(--bs-border-color);
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }

        .admin-card .card-header {
          background: var(--bs-card-bg);
          border-bottom: 1px solid var(--bs-border-color);
          padding: 14px 16px;
        }

        .users-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .user-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: var(--bs-gray-100);
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .user-item:hover {
          background: var(--bs-gray-200);
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 1;
        }

        .user-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .user-details {
          flex: 1;
        }

        .user-nickname {
          font-size: 13px;
          font-weight: 600;
          color: var(--bs-body-color);
        }

        .user-host {
          font-size: 11px;
          color: var(--bs-secondary-color);
        }

        .user-badges {
          display: flex;
          gap: 4px;
        }

        .user-status {
          display: flex;
          align-items: center;
        }

        .admin-actions {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .admin-action-btn {
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          padding: 10px 16px;
          text-align: left;
        }

        .auditor-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .filter-controls {
          display: flex;
          gap: 8px;
        }

        .logs-card {
          border: 1px solid var(--bs-border-color);
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }

        .logs-table {
          width: 100%;
        }

        .logs-header {
          display: grid;
          grid-template-columns: 150px 100px 150px 1fr 120px;
          gap: 12px;
          padding: 12px 16px;
          background: var(--bs-gray-100);
          border-bottom: 1px solid var(--bs-border-color);
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          color: var(--bs-secondary-color);
          letter-spacing: 0.5px;
        }

        .log-row {
          display: grid;
          grid-template-columns: 150px 100px 150px 1fr 120px;
          gap: 12px;
          padding: 12px 16px;
          border-bottom: 1px solid var(--bs-border-color);
          font-size: 12px;
          transition: background 0.2s ease;
        }

        .log-row:hover {
          background: var(--bs-gray-100);
        }

        .log-row:last-child {
          border-bottom: none;
        }

        .log-col-time,
        .log-col-type,
        .log-col-user,
        .log-col-action,
        .log-col-severity {
          display: flex;
          align-items: center;
        }

        .config-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 16px;
        }

        .config-card {
          border: 1px solid var(--bs-border-color);
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }

        .config-card .card-header {
          background: var(--bs-card-bg);
          border-bottom: 1px solid var(--bs-border-color);
          padding: 14px 16px;
        }

        .power-controls {
          display: flex;
          flex-direction: column;
          gap: 24px;
          align-items: center;
        }

        .power-status {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }

        .status-indicator-large {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.9;
          }
        }

        .status-text {
          text-align: center;
        }

        .status-label {
          font-size: 12px;
          color: var(--bs-secondary-color);
          margin-bottom: 4px;
        }

        .status-value {
          font-size: 24px;
          font-weight: 700;
        }

        .power-buttons {
          display: flex;
          gap: 12px;
          width: 100%;
        }

        .power-btn {
          flex: 1;
          border-radius: 10px;
          font-weight: 600;
          padding: 12px 24px;
        }

        .config-options {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .config-option {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background: var(--bs-gray-100);
          border-radius: 8px;
        }

        .config-option-info {
          flex: 1;
        }

        .config-option-label {
          font-size: 13px;
          font-weight: 600;
          color: var(--bs-body-color);
          margin-bottom: 4px;
        }

        .config-option-description {
          font-size: 11px;
          color: var(--bs-secondary-color);
        }

        @media (max-width: 768px) {
          .linkhub-portal {
            padding: 16px;
          }

          .stats-grid,
          .content-grid,
          .admin-controls-grid,
          .config-grid {
            grid-template-columns: 1fr;
          }

          .portal-tabs {
            flex-direction: column;
          }

          .logs-header,
          .log-row {
            grid-template-columns: 1fr;
            gap: 8px;
          }

          .logs-header {
            display: none;
          }

          .log-row > div:before {
            content: attr(data-label);
            font-weight: 600;
            margin-right: 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default LinkHubPortal;